import { api, writeDB } from './store';
import { Event, EventSalesStats } from './event';
import { TimeFrame } from '../util/util';
import { Category } from './category';
import { Image } from './asset';
import { PaginatedLoaderResponse, paginatedLoaderResponseFrom } from './data';

/* INTERFACES */
export interface TourDate {
  startDate: string;
  endDate: string;
}

export interface TourBase {
  id: string;
  slug?: string;
  name?: string;
  subtitle?: string;
  description?: string;
  excludeFromFeeds?: boolean;
  internalName?: string;
  startDate?: Date;
  endDate?: Date;
  mainCategory?: boolean;
  subtitleInShop?: boolean;
  order?: number;
  sortOrderHomepage?: number;
  sortOrderTicketTopLeft?: number;
  sortOrderTicketTopRight?: number;
  sortOrderTicketBottom?: number;
  comment?: string;
  artists?: Category[];
  images: Image[];
  events: Event[];
  createdAt?: Date;
  $permissions: string[];
  shopId: string;
  publishedFrom?: Date;
  publishedUntil?: Date;
}

export interface Tour extends TourBase {
  ticketImage?: Image;
  salesStats?: EventSalesStats;
  meta?: Record<string, JSONValue>;
}

export interface TourEdit extends TourBase {
  ticketImage?: string | null;
  artistIds?: string[];
  saleStats?: {
    availableCount?: number;
    unavailableCount?: number;
    blockedCount?: number;
    draftsCount?: number;
    bookedCount?: number;
  };
}

/* TOURS CREATE */
export async function addTour(model: Partial<TourEdit>): Promise<Tour> {
  const response = await api.createTour(model);
  const tour = response.data as unknown as Tour;
  writeDB.table('tours').insert(tour);
  return tour;
}

/* TOURS READ */
export async function getTours({
  page,
  perPage,
  timeframe,
  term,
  sortBy = 'start_date',
  sortDir = 'desc',
}: {
  term?: string;
  page?: number;
  perPage?: number;
  timeframe?: TimeFrame;
  sortBy?: string;
  sortDir?: string;
}): Promise<PaginatedLoaderResponse> {
  const response = await api.indexTours({
    page: (page || 0) + 1,
    term,
    perPage,
    ...(timeframe && timeframe[0] && timeframe[1]
      ? {
          startDate: timeframe[0],
          endDate: timeframe[1],
        }
      : {}),
    sortDir,
    sortBy,
  });
  writeDB.table('tours').upsert(response.data);
  return paginatedLoaderResponseFrom(response);
}

export async function getTour(tourId: string): Promise<void> {
  const response = await api.showTour({ tourId });
  writeDB.table('tours').upsert(response.data);
}

export async function getTourEvents({
  tourId,
  page,
  perPage,
  sortBy,
  sortDir,
}: {
  tourId: string;
  page?: number;
  perPage?: number;
  sortBy?: string;
  sortDir?: string;
}): Promise<PaginatedLoaderResponse> {
  const response = await api.indexTourEvents({ tourId, page: (page || 0) + 1, perPage, sortBy, sortDir });
  writeDB.table('events').upsert(response.data);
  return paginatedLoaderResponseFrom(response);
}

export async function getTourSalesStats(tourId: string): Promise<void> {
  const response = await api.showTourSalesStats({ tourId });
  writeDB.table('tours').update(tourId, { salesStats: response.data as unknown as EventSalesStats });
}

/* TOURS UPDATE */
export async function patchTour(id: string, tour: Partial<TourEdit>): Promise<void> {
  const response = await api.updateTour({
    tourId: id,
    ...tour,
  });
  // TODO: Update api model to match local interface
  const data = response.data as unknown as Tour;
  writeDB.table('tours').update(id, data);
}

/* TOURS DELETE */
export async function deleteTour(tourId: string): Promise<void> {
  await api.destroyTour({ tourId });
  writeDB.table('tours').delete(tourId);
}
